/**
 * Anzeigewerte für die Personalisierungsfelder.
 *
 * Der Store hält pro Person die Werte, die ANS BACKEND gehen — bei einem
 * Select also den Options-Key, bei einem Datum das value-format. In der
 * Zusammenfassung soll dagegen stehen, was der Käufer im Formular gesehen
 * hat. Beides steckt in der Felddefinition, die aus /shopsettings kommt:
 *
 *   Select   items: {option_yes: {de: 'Ja!', en: 'Yes!'}, …}
 *   Datum    value-format: 'YYYY-MM-DD', display-format: 'DD.MM.YYYY'
 *
 * Die Funktionen hier liegen bewusst außerhalb der Komponente: so sind sie
 * ohne gerenderten Shop prüfbar.
 */

/**
 * Lokalisierten Text aus einem Schemafeld holen.
 *
 * Das Schema liefert Beschriftungen als {de: …, en: …}, aber nicht immer:
 * ein static-Feld hat floating: '' (ein String). Deshalb beide Formen
 * annehmen und im Zweifel auf Deutsch und dann auf den ersten vorhandenen
 * Wert zurückfallen — eine leere Zelle wäre schlechter als die falsche
 * Sprache.
 */
export function localizedText(value, locale) {
    if (value === null || value === undefined) return ''
    if (typeof value === 'string') return value
    if (typeof value !== 'object') return String(value)
    if (value[locale] !== undefined) return value[locale]
    if (value.de !== undefined) return value.de
    const first = Object.values(value).find(v => typeof v === 'string')
    return first === undefined ? '' : first
}

/**
 * Ein Datum von einem Format ins andere bringen.
 *
 * Bewusst OHNE new Date(): 'YYYY-MM-DD' wird als UTC-Mitternacht gelesen,
 * und in Zeitzonen westlich von UTC zeigt toLocaleDateString() dann den
 * Vortag — bei einem Geburtsdatum ein echter Fehler. Stattdessen werden
 * die Stellen anhand des value-format ausgelesen und ins display-format
 * gesetzt; es wird also nur umsortiert, nicht gerechnet.
 *
 * Passt der Wert nicht zum Format, kommt er unverändert zurück.
 */
const TOKEN = /YYYY|YY|MM|DD|HH|mm|ss/g

export function reformatDate(value, valueFormat, displayFormat) {
    if (typeof value !== 'string' || !value) return value
    if (!valueFormat || !displayFormat) return value

    const parts = {}
    let cursor = 0
    let ok = true

    // value-format Stück für Stück abgehen und die Stellen mitschneiden.
    valueFormat.replace(/(YYYY|YY|MM|DD|HH|mm|ss)|([^YMDHms]+)/g, (match, token, literal) => {
        if (!ok) return match
        if (token) {
            const piece = value.substr(cursor, token.length)
            if (piece.length !== token.length || !/^\d+$/.test(piece)) { ok = false; return match }
            parts[token] = piece
            cursor += token.length
        } else if (literal) {
            if (value.substr(cursor, literal.length) !== literal) { ok = false; return match }
            cursor += literal.length
        }
        return match
    })

    if (!ok) return value
    // YY und YYYY sind austauschbar: das eine ist das gekürzte andere.
    if (parts.YYYY && !parts.YY) parts.YY = parts.YYYY.slice(-2)
    if (parts.YY && !parts.YYYY) parts.YYYY = parts.YY

    let missing = false
    const out = displayFormat.replace(TOKEN, token => {
        if (parts[token] === undefined) { missing = true; return token }
        return parts[token]
    })
    return missing ? value : out
}

/**
 * Label einer Select-Option.
 *
 * items kommt als Map {key: label} vom Backend. Vueform erlaubt aber auch
 * ein Array — entweder aus Strings oder aus {value,label}-Objekten — und
 * das Schema wird im Backend gepflegt, kann sich also ändern. Deshalb alle
 * drei Formen bedienen, statt auf die Map zu bauen.
 *
 * Unbekannter Wert: der Wert selbst. Lieber der Rohwert als eine leere
 * Zelle, sonst verschwindet eine Angabe aus der Bestellübersicht.
 */
export function optionLabel(items, value, locale) {
    if (items === null || items === undefined) return String(value)

    if (Array.isArray(items)) {
        const hit = items.find(item => {
            if (item === null || typeof item !== 'object') return String(item) === String(value)
            return String(item.value) === String(value)
        })
        if (hit === undefined) return String(value)
        if (hit === null || typeof hit !== 'object') return String(hit)
        return localizedText(hit.label !== undefined ? hit.label : hit.value, locale) || String(value)
    }

    if (typeof items === 'object') {
        const hit = Object.prototype.hasOwnProperty.call(items, value) ? items[value] : undefined
        if (hit === undefined) return String(value)
        return localizedText(hit, locale) || String(value)
    }

    return String(value)
}

/**
 * Der Wert, wie er in der Zusammenfassung stehen soll.
 *
 * Alles, was nicht Select oder Datum ist, geht unverändert durch — Namen,
 * E-Mail und Freitext sind schon die Anzeige.
 */
export function displayValue(field, value, locale) {
    if (value === null || value === undefined || value === '') return ''
    if (!field) return String(value)

    const type = field.type

    if (type === 'select' || type === 'multiselect' || type === 'tags' || field.items) {
        if (Array.isArray(value)) {
            return value.map(v => optionLabel(field.items, v, locale)).join(', ')
        }
        return optionLabel(field.items, value, locale)
    }

    if (type === 'date' || field.date) {
        return reformatDate(value, field['value-format'], field['display-format'])
    }

    if (typeof value === 'boolean') return value ? '✓' : ''
    if (Array.isArray(value)) return value.join(', ')
    if (typeof value === 'object') return localizedText(value, locale)

    return String(value)
}

/**
 * Nur die Felder, die überhaupt einen Wert tragen können.
 *
 * static-Felder sind reiner Erklärtext im Formular (hier ein Absatz zur
 * Ausweisnummer). Sie haben keinen Wert und ein leeres floating, erzeugten
 * in der Tabelle aber eine Spalte ohne Kopf und ohne Inhalt.
 */
export function valueFields(fields) {
    const out = {}
    if (!fields || typeof fields !== 'object') return out
    Object.keys(fields).forEach(key => {
        const field = fields[key]
        if (field && field.type === 'static') return
        out[key] = field
    })
    return out
}
